import React, { useState } from 'react';
import {
  Button,
  Typography,
  Paper,
  Box,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useNavigate } from 'react-router-dom';
import { RoutesWeb } from 'src/shared/interfaces/routes';
import { 
  associateCardToactivies, 
  uploadFileToS3,
  generateAttachmentFileKey,
  getSignedUrlForAttachmentWithKey,
  associateFileToActivities
} from './api';
import { FileMatching, Activity } from './interfaces';
import { formatCardDataForUpload } from './utils';
import Swal from 'sweetalert2';

interface Step5UploadProgressProps {
  fileMatching: FileMatching[];
  attachmentFiles: File[];
  selectedGroupData: {
    groupAssigned: string;
    activities: Activity[];
  };
  projectName: string;
  startDate: string;
  endDate: string;
}

export const Step5UploadProgress: React.FC<Step5UploadProgressProps> = ({
  fileMatching,
  attachmentFiles,
  selectedGroupData,
  projectName,
  startDate,
  endDate,
}) => {
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState<string>('');
  const [errors, setErrors] = useState<string[]>([]);
  const [completed, setCompleted] = useState(false);
  const [cardsUploadedCount, setCardsUploadedCount] = useState<number>(0);
  const [attachmentsUploadedCount, setAttachmentsUploadedCount] = useState<number>(0);
  const [attachmentsAssociationsCount, setAttachmentsAssociationsCount] = useState<number>(0);

  const handleStartUpload = async () => {
    setUploading(true);
    setProgress(0);
    setErrors([]);
    setCompleted(false);
    setCardsUploadedCount(0);
    setAttachmentsUploadedCount(0);
    setAttachmentsAssociationsCount(0);
    setCurrentStep('Preparando datos...');

    try {
      // Verificar que hay archivos para procesar
      const matchedActivities = fileMatching.filter(
        m => m.matchedFiles.length > 0 && m.status === 'matched'
      );

      if (matchedActivities.length === 0) {
        throw new Error('No hay cartas para asociar');
      }

      const totalFiles = matchedActivities.reduce(
        (total, m) => total + m.matchedFiles.length,
        0
      );

      if (totalFiles === 0) {
        throw new Error('No hay archivos de cartas para procesar');
      }

      // Paso 1: Parsear archivos .txt
      setCurrentStep(`Parseando ${totalFiles} archivo(s) de cartas...`);
      setProgress(5);

      let uploadData;
      const parseErrors: string[] = [];
      let cardsUploaded = 0;
      let totalCardsParsed = 0;

      try {
        uploadData = await formatCardDataForUpload(fileMatching, projectName);
        setProgress(20);
      } catch (error: any) {
        parseErrors.push(`Error parseando archivos: ${error.message}`);
        setErrors(parseErrors);
        // Si no se puede parsear nada, detener el proceso
        if (!uploadData || uploadData.length === 0) {
          throw error;
        }
      }

      // Si hay datos para subir, continuar
      if (uploadData && uploadData.length > 0) {
        // Verificar que hay datos válidos para subir
        totalCardsParsed = uploadData.reduce((total, item) => total + item.value.length, 0);
        
        if (totalCardsParsed === 0) {
          parseErrors.push('No se encontraron datos válidos en los archivos parseados');
          setErrors(parseErrors);
        } else {
          // Mostrar resumen de archivos parseados
          if (parseErrors.length > 0) {
            setCurrentStep(`Parseados ${totalCardsParsed} carta(s) de ${totalFiles} archivo(s) (algunos con errores)`);
          } else {
            setCurrentStep(`Parseados ${totalCardsParsed} carta(s) correctamente`);
          }
          setProgress(25);

          // Paso 2: Subir datos de cartas al endpoint
          setCurrentStep(`Subiendo datos de ${uploadData.length} actividad(es)...`);
          setProgress(30);

          try {
            const result = await associateCardToactivies(uploadData);
            setProgress(60);
            
            // Contar cartas subidas exitosamente
            if (result.success !== false && result.processed > 0) {
              // Calcular cuántas cartas se subieron basándose en las actividades procesadas
              const processedActivities = uploadData.slice(0, result.processed);
              cardsUploaded = processedActivities.reduce((total, item) => total + item.value.length, 0);
              setCardsUploadedCount(cardsUploaded);
            }
            
            if (result.errors && result.errors.length > 0) {
              // Hay errores pero algunos se procesaron
              setCurrentStep(`Procesados ${result.processed} de ${result.total} actividades (con errores)`);
              parseErrors.push(...result.errors);
            } else {
              setCurrentStep(`Cartas subidas correctamente: ${cardsUploaded} carta(s) de ${totalCardsParsed} procesada(s)`);
            }
          } catch (error: any) {
            parseErrors.push(`Error subiendo cartas: ${error.message}`);
            // Continuar con adjuntos aunque falle la subida de cartas
            setCurrentStep(`Error al subir cartas, continuando con adjuntos...`);
          }

          if (parseErrors.length > 0) {
            setErrors([...parseErrors]);
          }
        }
      } else {
        // No hay datos válidos, pero continuar con adjuntos si existen
        if (attachmentFiles.length === 0) {
          throw new Error('No se pudo parsear ningún archivo. Verifique que los archivos tengan el formato correcto.');
        }
      }

      // Paso 3: Subir adjuntos generales a S3 y asociarlos a actividades
      if (attachmentFiles.length > 0) {
        // Obtener actividades del grupo que tienen cartas (las mismas a las que se subieron cartas)
        const activitiesWithCards = selectedGroupData.activities.filter(
          (activity: Activity) => activity.shouldHasCardsLoaded === true
        );

        if (activitiesWithCards.length === 0) {
          parseErrors.push('No hay actividades con cartas para asociar los archivos adjuntos');
          setErrors(parseErrors);
        } else {
          setCurrentStep(`Subiendo ${attachmentFiles.length} archivo(s) adjunto(s)...`);
          
          let filesUploadedCount = 0;
          let totalAssociations = 0;
          const uploadErrors: string[] = [...parseErrors];

          for (let i = 0; i < attachmentFiles.length; i++) {
            const file = attachmentFiles[i];
            const progressBase = 60;
            const progressIncrement = 35 / attachmentFiles.length;
            
            try {
              // 1. Generar fileKey según formato requerido
              setCurrentStep(`Generando ruta para: ${file.name}...`);
              const fileKey = generateAttachmentFileKey(file.name, startDate, selectedGroupData.groupAssigned);
              
              // 2. Obtener signed URL
              setCurrentStep(`Obteniendo URL para: ${file.name}...`);
              const signedUrl = await getSignedUrlForAttachmentWithKey(
                fileKey,
                file.type || 'application/octet-stream'
              );
              
              // 3. Subir archivo a S3 (solo una vez)
              setCurrentStep(`Subiendo a S3: ${file.name}...`);
              await uploadFileToS3(signedUrl, file);
              
              // 4. Asociar archivo a todas las actividades con cartas
              setCurrentStep(`Asociando ${file.name} a ${activitiesWithCards.length} actividad(es)...`);
              const associateResult = await associateFileToActivities(
                fileKey,
                file.name,
                file.type || 'application/octet-stream',
                activitiesWithCards,
                startDate
              );

              // Registrar errores de asociación
              if (associateResult.errors && associateResult.errors.length > 0) {
                uploadErrors.push(...associateResult.errors.map(err => `[${file.name}] ${err}`));
              }

              // Contar asociaciones exitosas
              if (associateResult.associated > 0) {
                filesUploadedCount++;
                totalAssociations += associateResult.associated;
                setAttachmentsUploadedCount(filesUploadedCount);
                setAttachmentsAssociationsCount(totalAssociations);
              } else if (associateResult.errors && associateResult.errors.length > 0) {
                // Si hay errores pero no asociaciones exitosas, el archivo no se contabiliza como subido
                // pero ya se agregó a uploadErrors arriba
              }

              setProgress(progressBase + ((i + 1) * progressIncrement));
            } catch (error: any) {
              uploadErrors.push(`${file.name}: ${error.message}`);
            }
          }

          if (uploadErrors.length > parseErrors.length) {
            setErrors(uploadErrors);
          }

          setCurrentStep(
            `Subidos ${filesUploadedCount} de ${attachmentFiles.length} adjunto(s), asociados a ${totalAssociations} actividad(es)`
          );
        }
      } else {
        setProgress(95);
      }

      setProgress(100);
      setCurrentStep('Proceso completado');
      setCompleted(true);

      // Mostrar mensaje de éxito o advertencia según si hubo errores
      const hasErrors = errors.length > 0;
      let successMessage = '';
      
      if (cardsUploadedCount > 0) {
        successMessage = `${cardsUploadedCount} carta(s) dinámometricas subida(s) correctamente`;
        if (attachmentsUploadedCount > 0) {
          successMessage += `. ${attachmentsUploadedCount} archivo(s) adjunto(s) subido(s) y asociado(s) a ${attachmentsAssociationsCount} actividad(es)`;
        }
      } else if (attachmentsUploadedCount > 0) {
        successMessage = `${attachmentsUploadedCount} archivo(s) adjunto(s) subido(s) y asociado(s) a ${attachmentsAssociationsCount} actividad(es)`;
      } else {
        successMessage = 'Los archivos se han cargado correctamente';
      }

      Swal.fire({
        title: hasErrors ? 'Proceso completado con advertencias' : 'Éxito',
        text: hasErrors 
          ? `${successMessage} Pero hubo ${errors.length} error(es). Revise la lista de errores.`
          : successMessage,
        icon: hasErrors ? 'warning' : 'success',
        confirmButtonText: 'Aceptar',
      });
    } catch (error: any) {
      const allErrors = errors.length > 0 ? errors : [error.message];
      setErrors(allErrors);
      setCurrentStep('Error en el proceso');
      setProgress(0);
      Swal.fire({
        title: 'Error',
        text: error.message || 'Error al cargar los archivos',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleFinish = () => {
    navigate(RoutesWeb.gerencia_tecnica_operaciones.upload);
  };

  const cardCount = fileMatching.reduce((total, m) => {
    return total + (m.status === 'matched' ? m.matchedFiles.length : 0);
  }, 0);
  const attachmentCount = attachmentFiles.length;

  return (
    <Grid container className="dyn-cards-wizard-container" spacing={2}>
      <Paper className="dyn-cards-wizard-paper" elevation={2} sx={{ width: '100%' }}>
        <Typography className="dyn-cards-wizard-title" sx={{ fontSize: 16, fontWeight: 600, mb: 2 }}>
          PASO 5: SUBIR ARCHIVOS
        </Typography>

        <Box sx={{ height: '100%', overflow: 'scroll' }}>
        {/* Resumen */}
        <Box sx={{ mb: 2, p: 0.5, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Resumen</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary">Cuadrilla:</Typography>
              <Typography variant="body1" fontWeight="bold">{selectedGroupData.groupAssigned}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary">Proyecto:</Typography>
              <Typography variant="body1" fontWeight="bold">{projectName}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary">Cartas a asociar:</Typography>
              <Typography variant="body1" fontWeight="bold">{cardCount}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary">Adjuntos:</Typography>
              <Typography variant="body1" fontWeight="bold">{attachmentCount}</Typography>
            </Grid>
          </Grid>
        </Box>

       
        {/* Progreso */}
        {uploading && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>{currentStep}</Typography>
            <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 1 }} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {Math.round(progress)}%
            </Typography>
          </Box>
        )}

        {/* Errores */}
        {errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Errores durante la subida:</Typography>
            <List dense>
              {errors.map((error, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <ErrorIcon color="error" />
                  </ListItemIcon>
                  <ListItemText primary={error} />
                </ListItem>
              ))}
            </List>
          </Alert>
        )}

        {/* Éxito */}
        {completed && !uploading && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body1">
              {cardsUploadedCount > 0 && (
                <>
                  {cardsUploadedCount} carta(s) dinámometricas subida(s) correctamente
                  {attachmentsUploadedCount > 0 && (
                    <>.{' '}{attachmentsUploadedCount} archivo(s) adjunto(s) asociado(s) a {attachmentsAssociationsCount} actividad(es)</>
                  )}
                </>
              )}
              {cardsUploadedCount === 0 && attachmentsUploadedCount > 0 && (
                <>
                  {attachmentsUploadedCount} archivo(s) adjunto(s) subido(s) y asociado(s) a {attachmentsAssociationsCount} actividad(es)
                </>
              )}
              {cardsUploadedCount === 0 && attachmentsUploadedCount === 0 && (
                <>Proceso completado exitosamente</>
              )}
            </Typography>
          </Alert>
        )}
        </Box>

        {/* Botones */}
        <Grid container spacing={2} justifyContent="center" sx={{ mt: 0.1 }}>
          {!completed && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleStartUpload}
              disabled={uploading || cardCount === 0}
              size="large"
            >
              {uploading ? 'Subiendo...' : 'Iniciar Subida'}
            </Button>
          )}
          {completed && (
            <Grid container spacing={2} justifyContent="center">
              {errors.length > 0 && (
                <Grid>
                  <Button 
                    variant="contained" 
                    color="warning" 
                    onClick={handleStartUpload} 
                    size="large"
                    disabled={uploading}
                  >
                    Reintentar
                  </Button>
                </Grid>
              )}
              <Grid>
                <Button variant="contained" color="success" onClick={handleFinish} size="large">
                  Finalizar
                </Button>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Paper>
    </Grid>
  );
};

